import React from "react";

import { faChevronDown, faChevronUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faMask } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { sumBy } from "lodash";

import { anonymizeFeedbackUser, replaceLinks } from "~/feedback/utils";
import NameList from "~/ui/avatar/NameList";
import Avatar from "~/ui/legacy/Avatar";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import Checkbox from "~/ui/legacy/Checkbox";

import { Face, isFaceValue, isStarValue, RadioButton, Star, Thumb } from "../FormInputs";
import { adaptQuestionChoiceToValue } from "../ResponseForm";
import type { Question as QuestionProps } from "../ResponseForm/validation";
import StarRating from "./StarRating";

import stylesCheckboxInput from "~/feedback/FormInputs/css/CheckboxInput.module.css";

import styles from "./css/Question.module.css";

const NO_ANSWER_KEY = "noAnswers";

function AnonymousAvatar() {
	return (
		<div className={styles.anonymousAvatar}>
			<FontAwesomeIcon icon={faMask} className={styles.icon} />
		</div>
	);
}

export interface Choice {
	label: string;
	value: string | number;
}

export interface Answer {
	user: UserAttrs;
	value?: string | Choice | Choice[];
}

interface Props {
	question: QuestionProps;
	answers: Answer[];
	isAnonymous: boolean;
	currentUser: { id: string };
	feedbackId?: string;
}

interface State {
	moreTextExpanded: boolean;
}

class Question extends React.PureComponent<Props, State> {
	state: State = { moreTextExpanded: false };

	toggleMoreText = () => this.setState(({ moreTextExpanded }) => ({ moreTextExpanded: !moreTextExpanded }));

	renderNoAnswers = (noAnswers: Answer[]) => {
		const { isAnonymous } = this.props;
		const numNoAnswers = noAnswers.length;

		return (
			<div className={styles.textAnswer}>
				<Avatar
					initials={noAnswers.length}
					width="2.5em"
					uiAvatarOptions={{
						background: "E6EAED",
						color: "6F787D",
					}}
				/>
				<span className={styles.noAnswer}>
					{isAnonymous ? (
						`${numNoAnswers} other${numNoAnswers === 1 ? "" : "s"}`
					) : (
						<NameList
							maxLength={4}
							users={noAnswers.map(({ user }) => ({
								...user,
								fullName: user.fullName || user.firstName || user.email || "",
								image: typeof user.image === "string" ? user.image : undefined,
							}))}
						/>
					)}{" "}
					chose not to answer
				</span>
			</div>
		);
	};

	renderTextAnswer = (group: { [key: string]: Answer[] }) => {
		const { moreTextExpanded } = this.state;

		const showAll = group.answers.length <= 5 || moreTextExpanded;
		const answers = showAll ? group.answers : group.answers.slice(0, 5);

		return (
			<>
				{answers &&
					answers.map(answer => (
						<div
							key={answer.user.id}
							className={cx(styles.textAnswer, { [styles.hasMore]: !showAll || moreTextExpanded })}
						>
							{this.props.isAnonymous ? <AnonymousAvatar /> : <Avatar user={answer.user} link={false} width="2.5em" />}
							<span>{replaceLinks(answer.value as string)}</span>
						</div>
					))}
				{group[NO_ANSWER_KEY].length > 0 && showAll && this.renderNoAnswers(group[NO_ANSWER_KEY])}
				{!showAll && (
					<span className={styles.moreText} onClick={this.toggleMoreText}>
						Show more <FontAwesomeIcon icon={faChevronDown} />
					</span>
				)}
				{moreTextExpanded && (
					<span className={styles.moreText} onClick={this.toggleMoreText}>
						Show less <FontAwesomeIcon icon={faChevronUp} />
					</span>
				)}
			</>
		);
	};

	renderChoice = ({
		child,
		key,
		label,
		answers = [],
		totalAnswersCount,
	}: {
		child: React.ReactNode;
		key: string;
		label?: string;
		answers: Answer[];
		totalAnswersCount: number;
	}) => {
		const answersCount = answers ? answers.length : 0;
		const percent = totalAnswersCount ? (answersCount / totalAnswersCount) * 100 : 0;
		const { isAnonymous } = this.props;

		return (
			<div className={styles.grid} key={key}>
				{child}
				{label && <div className={styles.label}>{label}</div>}
				<div className={cx(styles.progressBar, { [styles.withLabel]: !!label })}>
					<div style={{ width: `${percent}%` }}></div>
				</div>
				<div className={styles.responses}>{`${answersCount} Response${answersCount === 1 ? "" : "s"}`}</div>
				<div className={styles.users}>
					{answersCount ? (
						<NameList
							maxLength={4}
							users={answers.map(({ user }) => {
								if (isAnonymous && user.id === this.props.currentUser.id)
									return anonymizeFeedbackUser(this.props.feedbackId || "", this.props.currentUser);
								return {
									...user,
									fullName: user.fullName || user.firstName || user.email || "",
									image: typeof user.image === "string" ? user.image : undefined,
								};
							})}
						/>
					) : (
						""
					)}
				</div>
				<div className={styles.percentage}>{`${parseFloat(percent.toFixed(1))}%`}</div>
			</div>
		);
	};

	renderCheckboxAnswer = (group: { [key: string]: Answer[] }, totalAnswersCount: number) => {
		const { question } = this.props;

		if (question.type !== "choice" || question.appearance !== "checkbox") {
			return null;
		}

		return question.choices.map(({ label, value }) => {
			return this.renderChoice({
				child: (
					<Checkbox
						green
						className={cx(styles.choice, stylesCheckboxInput.readonly)}
						key={value}
						value={value}
						checked={group[value].length > 0}
						readOnly
					/>
				),
				key: `choice-${value}`,
				label,
				answers: group[value],
				totalAnswersCount,
			});
		});
	};

	renderRadioAnswer = (group: { [key: string]: Answer[] }, totalAnswersCount: number) => {
		const { question } = this.props;

		if (question.type !== "choice" || question.appearance !== "radio") {
			return null;
		}

		return question.choices.map(({ label, value }) => {
			return this.renderChoice({
				child: (
					<RadioButton
						className={styles.choice}
						key={value}
						value={value}
						checked={group[value].length > 0}
						readOnly
						disabled
					/>
				),
				key: `choice-${value}`,
				label,
				answers: group[value],
				totalAnswersCount,
			});
		});
	};

	renderThumbsAnswer = (group: { [key: string]: Answer[] }, totalAnswersCount: number) => {
		const { question } = this.props;

		if (question.type !== "choice" || question.appearance !== "thumbs") {
			return null;
		}

		return (
			<>
				{this.renderChoice({
					child: <Thumb className={styles.choice} value="up" selected={group[10].length > 0} readonly />,
					key: "choice-up",
					answers: group[10],
					totalAnswersCount,
				})}
				{this.renderChoice({
					child: <Thumb className={styles.choice} value="down" selected={group[0].length > 0} readonly />,
					key: "choice-down",
					answers: group[0],
					totalAnswersCount,
				})}
			</>
		);
	};

	renderStarsAnswer = (group: { [key: string]: Answer[] }, totalAnswersCount: number) => {
		const { question, answers } = this.props;

		if (question.type !== "choice" || question.appearance !== "stars") {
			return null;
		}

		const progress = totalAnswersCount ? sumBy(answers, "value.value") / (10 * totalAnswersCount) : 0;

		return (
			<>
				<StarRating progress={progress} />
				{[...question.choices].reverse().map(answer => {
					const { label, value } = answer;
					const starValue = adaptQuestionChoiceToValue(question, answer);

					if (!isStarValue(starValue)) return null;

					return this.renderChoice({
						child: (
							<Star
								readonly
								showValue
								key={label}
								value={starValue}
								selected={group[value].length > 0}
								className={cx(styles.choice, styles.star)}
							/>
						),
						key: `choice-${value}`,
						answers: group[value],
						totalAnswersCount,
					});
				})}
			</>
		);
	};

	renderSmilesAnswer = (group: { [key: string]: Answer[] }, totalAnswersCount: number) => {
		const { question } = this.props;

		if (question.type !== "choice" || question.appearance !== "smiles") {
			return null;
		}

		return [...question.choices].reverse().map(choice => {
			const { value } = choice;
			const faceValue = adaptQuestionChoiceToValue(question, choice);

			if (!isFaceValue(faceValue)) {
				return null;
			}

			return this.renderChoice({
				child: <Face key={value} className={styles.choice} type={faceValue} selected={group[value].length > 0} />,
				key: `choice-${value}`,
				answers: group[value],
				totalAnswersCount,
			});
		});
	};

	render() {
		const { question, answers } = this.props;
		const { title: originalTitle } = question;

		const isIndented = originalTitle[0] === ">";
		const title = isIndented ? originalTitle.slice(1).trim() : originalTitle;
		const titleElement = replaceLinks(title);

		let groupInitialValue: { [key: string]: Answer[] } = { [NO_ANSWER_KEY]: [] };

		if (question.type === "text") {
			groupInitialValue = { ...groupInitialValue, answers: [] };
		} else if (question.type === "choice") {
			question.choices.forEach((c: (typeof question.choices)[number]) => {
				groupInitialValue[c.value] = [];
			});
		}

		const group = answers.reduce((obj, answer) => {
			if (question.type === "text") {
				// Text answer
				obj[answer.value ? "answers" : NO_ANSWER_KEY].push(answer);
			} else if (question.type === "choice") {
				if (Array.isArray(answer.value)) {
					// Checkbox answer
					answer.value.forEach(v => {
						obj[v.value ? v.value : NO_ANSWER_KEY].push(answer);
					});
				} else if (typeof answer.value === "object" && "value" in answer.value) {
					// Radio / Thumbs / Stars / Smiles answer
					obj[answer.value.value].push(answer);
				} else {
					obj[NO_ANSWER_KEY].push(answer);
				}
			}

			return obj;
		}, groupInitialValue);

		const noAnswersCount = group[NO_ANSWER_KEY].length;
		const totalAnswersCount = answers.length - noAnswersCount;

		return (
			<div className={cx(styles.wrapper, { [styles.indented]: isIndented })}>
				<div
					className={cx(styles.title, {
						[styles.titleHeading]: question.appearance === "text",
						[styles.titleSubHeading]: question.appearance === "sub-text",
					})}
				>
					{titleElement}
				</div>
				{question.type === "text" && this.renderTextAnswer(group)}
				{question.type === "choice" && (
					<>
						{this.renderRadioAnswer(group, totalAnswersCount)}
						{this.renderCheckboxAnswer(group, totalAnswersCount)}
						{this.renderThumbsAnswer(group, totalAnswersCount)}
						{this.renderStarsAnswer(group, totalAnswersCount)}
						{this.renderSmilesAnswer(group, totalAnswersCount)}
						{noAnswersCount > 0 && this.renderNoAnswers(group[NO_ANSWER_KEY])}
					</>
				)}
			</div>
		);
	}
}

export default Question;
